/**
 * Utility functions to send messages between:
 * - Extension content script
 * - Extension background page
 * - embeddedWebReader.js or importerBookmarklet.js (running within 3rd party website)
 * - bookmarklet.js or bookmarkletAddDocument.js (running within readlang.com iframe)
 */
const readlangMessageType = "readlangMessage";

const verboseMode = false;

/**
 * This will be set by the embeddedWebReader with a handle to the iFrame, allowing us to send
 * messages to it.
 */
let webReaderIFrame: any;
let importerIFrame: any;

type Context =
  // Chrome extension
  | "extension"

  // Readlang script running in 3rd party website
  // XXX Rename to "embeddedScript" since it also applies to importerBookmarklet??
  | "embeddedWebReader"

  // Readlang script running in readlang.com iframe
  | "webReaderIFrame"

  // Readlang script running in readlang.com iframe
  | "importerIFrame";

/**
 * @param to - Which context to send the message to
 * @param message - The message
 * @param data - Optional data, will be JSON stringified
 * @param requestId - Optional, only relevant for extension context, used to match request/response
 */
export const postMessage = (
  to: Context,
  message: string,
  data?: object,
  requestId?: string
) => {
  if (verboseMode) {
    console.log(`Posting message to ${to}: ${message}`);
  }
  switch (to) {
    case "extension":
      window.postMessage(
        JSON.stringify({
          type: readlangMessageType,
          to,
          message: "backgroundPageMessage",
          requestId,
          data: {
            message,
            data,
          },
        }),
        // TODO: Should this be window.location.origin?
        "*"
      );
      break;

    case "embeddedWebReader":
      top.postMessage(
        JSON.stringify({
          type: readlangMessageType,
          to,
          message,
          data,
        }),
        "*"
      ); // TODO: Parent origin?
      break;

    case "webReaderIFrame":
      if (!webReaderIFrame) {
        throw Error("web reader iFrameWindow not set");
      }

      webReaderIFrame.postMessage(
        JSON.stringify({
          type: readlangMessageType,
          to,
          message,
          data,
        }),
        "*"
      );
      break;

    case "importerIFrame":
      if (!importerIFrame) {
        throw Error("importer iFrameWindow not set");
      }

      importerIFrame.postMessage(
        JSON.stringify({
          type: readlangMessageType,
          to,
          message,
          data,
        }),
        "*"
      );
      break;
  }
};

export const setWebReaderIFrame = (newIFrameWindow: any) => {
  webReaderIFrame = newIFrameWindow;
};

export const setImporterIFrame = (newIFrameWindow: any) => {
  importerIFrame = newIFrameWindow;
};

// eslint-disable-next-line no-unused-vars
type MessageHandler = (message: string, data: any, rawEvent?: any) => void;

export const messageListener = (context: Context, handler: MessageHandler) => {
  let eventListener;
  if ("addEventListener" in window) {
    eventListener = window.addEventListener;
  } else if ("attachEvent" in window) {
    // @ts-ignore
    eventListener = window.attachEvent;
  } else {
    alert(
      "The Readlang bookmarklet is not working on this page using this browser." +
        "\n\nTry using Google Chrome instead."
    );
  }

  eventListener("message", (event) => {
    let data;
    try {
      data = JSON.parse(event.data);
    } catch (error) {
      // Ignore non-JSON messages.
      return;
    }

    if (data?.type !== readlangMessageType) {
      // console.log("Ignoring invalid message", event.data);
      return;
    }

    if (data.to !== context) {
      // console.log(
      //   `Ignoring message for different context. ${data.to} !== ${context}`
      // );
      return;
    }

    if (context === "extension") {
      throw Error("Extension requests handled by code in contentScript.js");
    }

    if (verboseMode) {
      console.log(
        `Received message to ${context}: ${data.message} (from ${event.origin})`
      );
    }

    handler(data.message, data.data, event);
  });
};
